import React, {
  useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';

// Utils
import frLocale from 'date-fns/locale/fr';
import { useTranslation } from 'react-i18next';
import {
  concat, isEmpty, isNil, sortBy,
} from 'lodash';
import {
  addDays,
  startOfWeek,
  format,
  endOfWeek,
  addWeeks,
  isEqual,
  subWeeks,
  isAfter,
  isBefore,
  addYears,
  parse,
  differenceInWeeks,
} from 'date-fns';
import { useMutation, useQuery } from 'react-query';
import { useAppContext } from '../../../../store/AppContext';
import cn from '../../../../utils/tools/cn';
import dayIdTool from '../../../../utils/tools/dayId';
import useNotification from '../../../../utils/hooks/useNotification';
import useModal from '../../../../utils/hooks/useModal';

// Services
import useInventoryService from '../../../../services/inventoryService';

// Components
import InventoryTable from '../../../tables/InventoryTable/InventoryTable';
import EmptyList from '../../../organisms/EmptyList/EmptyList';
import Checkbox from '../../../atoms/Checkbox/Checkbox';
import SelectField from '../../../atoms/SelectField/SelectField';
import BatchEdition from '../BatchEdition/BatchEdition';
import TableInputNumber from '../../../atoms/TableInputNumber/TableInputNumber';

import styles from './Stocks.module.scss';

const inventoryType = {
  camps: 'CAMP',
  extras: 'EXTRA',
  accommodations: 'ACCOMMODATION',
};

function Stocks({ products, type }) {
  const { t } = useTranslation();
  const [{ establishment, notifications }] = useAppContext();
  const { displayNotification } = useNotification();
  const { displayModal } = useModal();

  const { getInventories, putInventoryQuantity, postInventoryStatus } = useInventoryService();

  const [inventories, setInventories] = useState({});

  const establishmentCreationDate = useMemo(() => (
    establishment?.creationDate ? parse(establishment.creationDate, 'yyyy-MM-dd', new Date()) : null
  ), [establishment]);

  const getStartOfWeek = (date) => startOfWeek(date, { weekStartsOn: 1 });
  const getEndOfWeek = (date) => endOfWeek(date, { weekStartsOn: 1 });

  const minWeekLimit = useMemo(() => (
    getStartOfWeek(establishmentCreationDate || new Date())
  ), [establishmentCreationDate]);
  const maxWeekLimit = useMemo(() => getStartOfWeek(subWeeks(addYears(new Date(), 1), 1)), []);

  const defaultWeekMonday = useMemo(() => (getStartOfWeek(new Date())), []);
  const [activeWeekMonday, setActiveWeekMonday] = useState(getStartOfWeek(new Date()));
  const activeWeekSunday = useMemo(
    () => (activeWeekMonday ? getEndOfWeek(activeWeekMonday) : null),
    [activeWeekMonday],
  );

  const getFormatStart = (date) => format(getStartOfWeek(date), 'dd/MM/yyyy');
  const getFormatEnd = (date) => format(getEndOfWeek(date), 'dd/MM/yyyy');
  const getWeekLabel = (date) => (
    `${t('logistic.week')} ${format(date, 'I')} (${getFormatStart(date)} - ${getFormatEnd(date)})`
  );

  const weeksOptions = useMemo(() => (
    concat(
      Array.from(Array(
        isBefore(minWeekLimit, new Date()) && differenceInWeeks(getStartOfWeek(new Date()), minWeekLimit) > 0
          ? (differenceInWeeks(getStartOfWeek(new Date()), minWeekLimit))
          : 0,
      ).keys()).map(
        (_, index) => ({
          label: getWeekLabel(addWeeks(minWeekLimit, index)),
          value: getStartOfWeek(addWeeks(minWeekLimit, index)),
        }),
      ),
      Array.from(Array(52).keys()).map(
        (_, index) => ({
          label: getWeekLabel(addWeeks(new Date(), index)),
          value: getStartOfWeek(addWeeks(new Date(), index)),
        }),
      ),
    )
  ), [minWeekLimit]);

  const updateQuantityInInventory = (productId, dayId, quantity) => {
    if (inventories?.[productId]?.[dayId]?.id) {
      setInventories((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [dayId]: {
            ...prev[productId][dayId],
            quantity,
          },
        },
      }));
    }
  };

  const updateStatusInInventory = (productId, dayId, isActive) => {
    if (inventories?.[productId]?.[dayId]?.id) {
      setInventories((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [dayId]: {
            ...prev[productId][dayId],
            isActive,
          },
        },
      }));
    }
  };

  const {
    isLoading: putQuantityIsLoading,
    mutateAsync: putQuantityMutateAsync,
  } = useMutation(
    ({ inventoryId, quantity }) => putInventoryQuantity({ inventoryId, quantity }),
    {
      onMutate: (updated) => {
        if (updated?.productId && updated?.dayId) {
          updateQuantityInInventory(updated.productId, updated.dayId, updated.quantity);
        }
      },
      onError: (_, updated) => {
        if (!notifications?.length) {
          displayNotification({
            message: t('stocks.error.quantity'),
            color: 'error',
          });
        }
        updateQuantityInInventory(updated.productId, updated.dayId, updated.prevValue);
      },
    },
  );

  const {
    isLoading: putStatusIsLoading,
    mutate: postStatusMutate,
    mutateAsync: postStatusMutateAsync,
  } = useMutation(
    ({ inventoryId, isActive }) => postInventoryStatus({ inventoryId, isActive }),
    {
      onMutate: (updated) => {
        if (updated?.productId && updated?.dayId) {
          updateStatusInInventory(updated.productId, updated.dayId, updated.isActive);
        }
      },
      onError: (_, updated) => {
        if (!notifications?.length) {
          displayNotification({
            message: t('stocks.error.status'),
            color: 'error',
          });
        }
        updateStatusInInventory(updated.productId, updated.dayId, updated.prevValue);
      },
    },
  );

  const { isFetching } = useQuery(
    ['getInventories', activeWeekSunday],
    () => getInventories({
      type: inventoryType[type],
      start: dayIdTool.getDayIdFromDate(activeWeekMonday || defaultWeekMonday),
      end: dayIdTool.getDayIdFromDate(activeWeekSunday || getEndOfWeek(defaultWeekMonday)),
    }),
    {
      enabled: !isNil(inventoryType[type]),
      onSuccess: (res) => {
        const inventoriesRaw = res?.data?.inventories || [];
        const inventoriesByProduct = {};
        products.forEach(({ id }) => {
          const inv = inventoriesRaw?.filter((inventory) => inventory.productId === id);
          const invByDay = {};
          inv?.forEach(({
            date, id: inventoryId, quantity, isActive, soldQuantity,
          }) => {
            invByDay[date] = {
              id: inventoryId,
              quantity,
              isActive,
              soldQuantity,
            };
          });
          inventoriesByProduct[id] = invByDay;
        });
        setInventories(inventoriesByProduct);
      },
    },
  );

  const handleChangeQuantity = async (inventoryId, quantity, productId, dayId, prevValue) => {
    if (!inventoryId || isNil(quantity) || !productId || quantity < 0) return;

    await putQuantityMutateAsync({
      inventoryId,
      quantity: +quantity <= 9000 ? +quantity : 9000,
      productId,
      dayId,
      prevValue,
    });
  };

  const handleChangeStatus = (inventoryId, isActive, productId, dayId, prevValue) => {
    if (inventoryId && !isNil(isActive) && productId) {
      postStatusMutate({
        inventoryId,
        isActive,
        productId,
        dayId,
        prevValue,
      });
    }
  };

  const handleColumnAction = (dayId, isActive, productIds) => {
    if (productIds?.length) {
      productIds.forEach((productId) => {
        const inventoryId = inventories[productId]?.[dayId]?.id;
        const prevValue = inventories[productId]?.[dayId]?.isActive;
        handleChangeStatus(inventoryId, isActive, productId, dayId, prevValue);
      });
    }
  };

  const handleRowAction = (productId, isActive) => {
    if (!isEmpty(inventories)) {
      const inventoriesForProduct = inventories[productId];
      if (!isEmpty(inventoriesForProduct)) {
        Object.keys(inventoriesForProduct).forEach((dayId) => {
          if (inventoriesForProduct[dayId]?.id) {
            const prevValue = inventoriesForProduct[dayId].isActive;
            handleChangeStatus(inventoriesForProduct[dayId].id, isActive, productId, dayId, prevValue);
          }
        });
      }
    }
  };

  const getCell = (productId, inventory, dayId) => (
    inventory?.id ? (
      <div className={cn([styles.input_cell, 'd-flex a-center ws-nowrap'])}>
        <div className={cn([styles.checkbox, 'm-r-6 mobile-m-l-10 mobile-m-r-0'])}>
          <Checkbox
            id={`checkbox_${inventory.id}`}
            isChecked={inventory.isActive}
            onChange={(value) => handleChangeStatus(
              inventory.id,
              value?.target?.checked,
              productId,
              dayId,
              !(value?.target?.checked),
            )}
            disabled={putStatusIsLoading}
          />
        </div>

        <TableInputNumber
          isFaded={!inventory.isActive}
          staticValue={`${inventory.soldQuantity} /`}
          value={inventory.quantity}
          onChange={async (value) => {
            await handleChangeQuantity(
              inventory.id,
              value,
              productId,
              dayId,
              inventory.quantity,
            );
          }}
          isDisabled={putQuantityIsLoading}
          max={9000}
          min={0}
        />
      </div>
    ) : null
  );

  const isAllRowCellsChecked = useCallback((productId) => {
    if (!isEmpty(inventories) && productId && !isEmpty(inventories?.[productId])) {
      const itemsToCheck = Object.keys(inventories[productId])
        .filter((dayId) => !isNil(inventories[productId][dayId]?.id))
        .map((key) => inventories[productId][key]?.isActive);
      return itemsToCheck?.length && itemsToCheck.every((value) => value === true);
    }

    return false;
  }, [inventories]);

  const getProductRow = (product) => ({
    id: product.id,
    name: (
      <label
        className="d-flex a-center c-pointer mobile-d-row-reverse"
        htmlFor={`productAction_${product.id}`}
        data-search={product.name}
      >
        <Checkbox
          id={`productAction_${product.id}`}
          isChecked={isAllRowCellsChecked(product.id)}
          onChange={(value) => handleRowAction(
            product.id,
            value?.target?.checked,
          )}
          disabled={putStatusIsLoading}
        />

        <span className="m-l-10 mobile-m-r-10">
          {product.name}
        </span>
      </label>
    ),
    day_0: getCell(
      product.id,
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 0))],
      dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 0)),
    ),
    day_1: getCell(
      product.id,
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 1))],
      dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 1)),
    ),
    day_2: getCell(
      product.id,
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 2))],
      dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 2)),
    ),
    day_3: getCell(
      product.id,
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 3))],
      dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 3)),
    ),
    day_4: getCell(
      product.id,
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 4))],
      dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 4)),
    ),
    day_5: getCell(
      product.id,
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 5))],
      dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 5)),
    ),
    day_6: getCell(
      product.id,
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 6))],
      dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, 6)),
    ),
  });

  const getColumns = useCallback(() => {
    const daysOfWeek = Array.from(Array(7).keys()).map((_, index) => {
      const date = addDays(activeWeekMonday || defaultWeekMonday, index);

      return {
        key: `day_${index}`,
        title: (
          <div className="d-flex d-col fs-14 ws-nowrap p-t-10 p-b-10">
            <span className={cn([styles.day, 'fs-12 m-b-2'])}>
              {format(date, 'E', { locale: frLocale })}
            </span>
            {format(date, 'dd/MM/yyyy')}
          </div>
        ),
        dataColumn: format(date, 'E dd/MM/yyyy', { locale: frLocale }),
        width: 140,
        isSortable: false,
        actionOnColumn: true,
        dayId: dayIdTool.getDayIdFromDate(date),
      };
    });

    return concat([{
      key: 'name',
      title: t(`logistic.table.columns.${type}`),
      isSortable: false,
      bold: true,
      sticky: true,
      width: 200,
    }], daysOfWeek);
  }, [activeWeekMonday]);

  const handleGoToPrevWeek = () => {
    const dest = subWeeks(activeWeekMonday || defaultWeekMonday, 1);
    if (isAfter(dest, minWeekLimit) || isEqual(dest, minWeekLimit)) {
      setActiveWeekMonday(dest);
    }
  };

  const handleGoToNextWeek = () => {
    const dest = addWeeks(activeWeekMonday || defaultWeekMonday, 1);
    if (isBefore(dest, maxWeekLimit) || isEqual(dest, maxWeekLimit)) {
      setActiveWeekMonday(dest);
    }
  };

  const handleMultipleEdition = () => {
    displayModal({
      maxWidth: 455,
      title: t('batch.title.stocks'),
      content: (
        <BatchEdition
          products={
            products?.length
              ? sortBy(products.filter((product) => !isEmpty(inventories?.[product.id])), ['name'])
              : []
          }
          productType={type}
          inventoryType={inventoryType[type]}
          postStatusMutateAsync={postStatusMutateAsync}
          putQuantityMutateAsync={putQuantityMutateAsync}
        />
      ),
    });
  };

  return (
    <div>
      {products?.length ? (
        <InventoryTable
          isSearchable
          searchPlaceholder={t(`logistic.search.${type}`)}
          rows={
            sortBy(products
              .filter((product) => !isEmpty(inventories?.[product.id])), ['name'])
              .map((product) => getProductRow(product))
          }
          columns={getColumns()}
          filters={(
            <div className="input_max">
              <SelectField
                id="stocksActiveWeek"
                name="activeWeek"
                options={weeksOptions}
                label={t('logistic.week')}
                value={weeksOptions?.find((opt) => isEqual(opt.value, activeWeekMonday)) || null}
                onChange={(value) => setActiveWeekMonday(value?.value || null)}
              />
            </div>
          )}
          isLoading={isFetching}
          handleColumnAction={handleColumnAction}
          inventories={inventories}
          isColumnActionLoading={putStatusIsLoading}
          onNavLeft={handleGoToPrevWeek}
          onNavRight={handleGoToNextWeek}
          onMultipleEditionClick={handleMultipleEdition}
          multipleEditionLabel={t('batch.title.stocks')}
          isDisabledNavLeft={isBefore(subWeeks(activeWeekMonday || defaultWeekMonday, 1), minWeekLimit)}
          isDisableNavRight={isAfter(addWeeks(activeWeekMonday || defaultWeekMonday, 1), maxWeekLimit)}
        />
      ) : (
        <EmptyList
          imageTheme="products"
          title={t('stocks.empty.title')}
          text={t('stocks.empty.text')}
        />
      )}
    </div>
  );
}

Stocks.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
  })),
  type: PropTypes.oneOf(['camps', 'extras', 'accommodations']).isRequired,
};

Stocks.defaultProps = {
  products: [],
};

export default Stocks;
